import type { ReactNode } from 'react';
import { useRouteLoaderData } from 'react-router-dom';
import type { AdminMeResponse, Permission } from '@immo/api-contract';

const PROTECTED_ROUTE_ID = 'admin-protected';

/**
 * Read the current admin's permissions from the protected-route loader data.
 * `'PUBLIC'` always resolves to true; every other permission is a set
 * lookup against the resolved admin's permission list.
 *
 * Default-deny: if the loader hasn't populated yet (e.g. inside an unprotected
 * route or during hydration), `useCan` returns `false` for everything except
 * `PUBLIC`. The backend is the authoritative gate; the web-app uses this
 * to hide controls per ADR 0015.
 */
export function useCan(permission: Permission): boolean {
  const data = useRouteLoaderData(PROTECTED_ROUTE_ID) as AdminMeResponse | undefined;

  if (permission === 'PUBLIC') return true;

  if (data === undefined) return false;

  return data.user.permissions.includes(permission);
}

export function RequirePermission(props: { permission: Permission; children: ReactNode }): ReactNode {
  return useCan(props.permission) ? props.children : null;
}

/** The current admin's display name, for an optimistic row that has to name its issuer before the next poll confirms it. */
export function useCurrentAdminName(): string | null {
  const data = useRouteLoaderData(PROTECTED_ROUTE_ID) as AdminMeResponse | undefined;

  if (data === undefined) return null;

  const { firstName, lastName, email } = data.user;
  const full = [firstName, lastName].filter((part): part is string => part !== null).join(' ');

  return full.length > 0 ? full : email;
}
